// @ts-strict-ignore
import React from 'react';
import type { ColumnDef } from '@usealloy/component-library';
import { Icon, IconName } from '@usealloy/component-library';
import { BankAccountStatusBadge } from './BankAccountStatusBadge';
import { formatTimestamp } from '~/helpers/formatting';
import { formatCurrencyIntl } from '~/helpers/i18n';
import { components } from 'react-select';
import type { OptionProps, SingleValueProps } from 'react-select';
import type { BankAccount, TypeOption } from './types';

export const ACCOUNT_STATUS_OPTIONS: TypeOption[] = [
  { value: 'Active', label: 'Active' },
  { value: 'Deactivated', label: 'Deactivated' },
  { value: 'Frozen', label: 'Frozen' },
];

const noContent = label => <Icon aria-label={label} icon={IconName.HorizontalLine} size="small" />;

export const ACCOUNT_TABLE_COLUMNS: ColumnDef<BankAccount>[] = [
  {
    maxSize: 175,
    id: 'account_name',
    enableSorting: false,
    header: 'Name',
    cell: ({ row: { original } }: { row: { original } }) => (
      original?.bank_account?.account_name ? `${original.bank_account.account_name} Account` : noContent('no account name')),
  },
  {
    maxSize: 130,
    id: 'account_number',
    enableSorting: false,
    header: 'Account Number',
    cell: ({ row: { original } }: { row: { original } }) => (
      original?.bank_account?.account_number ? `${original.bank_account.account_number}` : noContent('no account number')),
  },
  {
    maxSize: 140,
    id: 'account_type_status',
    enableSorting: false,
    header: 'Status',
    cell: ({ row: { original } }: { row: { original } }) => (
      original?.bank_account?.account_type_status
        ? <BankAccountStatusBadge status={original.bank_account.account_type_status} />
        : noContent('no account status')
    ),
  },
  {
    maxSize: 110,
    id: 'opened',
    enableSorting: false,
    header: 'Date Opened',
    cell: ({ row: { original } }: { row: { original } }) => (
      original?.bank_account?.opening_date
        ? `${formatTimestamp(original.bank_account.opening_date, 'MMM D, YYYY')}`
        : noContent('no account opened date')
    ),
  },
  {
    maxSize: 125,
    id: 'account_balance',
    enableSorting: false,
    header: 'Current Balance',
    cell: ({ row: { original } }: { row: { original } }) => {
      let formattedBalance: string | JSX.Element = noContent('no current balance information');
      if (original?.current_snapshot?.account_balance) {
        const signDisplay = original.bank_account.account_type === 'credit' ? 'never' : 'auto';
        const accountBalanceData = formatCurrencyIntl(original.current_snapshot.account_balance,
          { alphabeticCurrencyCode: original.bank_account.currency },
          { signDisplay },
        );
        formattedBalance = accountBalanceData.formattedNumber;
      }
      return formattedBalance;
    },
  },
  {
    id: 'transaction_count',
    enableSorting: false,
    header: 'Transaction Count',
    cell: ({ row: { original } }: { row: { original } }) => (
      typeof original?.transaction_count === 'number' ? original.transaction_count : noContent('no transaction information')),
  },
];

export const AccountStatusOption = ({ data, children, ...restProps }: OptionProps<any, true>) => (
  <components.Option className="d-flex align-items-center" data={data} {...restProps}>
    <BankAccountStatusBadge status={data.value} />
  </components.Option>
);

export const AccountStatusSingleOption = (
  { data, children, ...restProps }: SingleValueProps<any>,
) => (
  <components.SingleValue
    className="d-flex align-items-center"
    data={data}
    {...restProps}
  >
    <BankAccountStatusBadge status={data.value} />
  </components.SingleValue>
);
